import { t } from '@lingui/core/macro'
import type { FC, RefObject } from 'react'

import { RequestError } from '../../../components/api/extractHttpError'
import { AskCopilotChat } from '../AskCopilot/AskCopilotChat'
import { AskCopilotError } from '../AskCopilot/AskCopilotError'
import { getFriendlyError } from '../AskCopilot/errorMessages'
import type { VoiceMailResponse } from './useVoiceMails'

type Props = {
  voiceMailData: VoiceMailResponse
  customer: string
  containerRef?: RefObject<HTMLElement | null>
}

const buildRecommendedReply = (
  question: string | null,
  reply: string | null,
) => {
  if (reply == null) return reply
  const safeQuestion = question ?? ''
  return `**${t`Question:`}** ${safeQuestion}\n\n${reply}`
}

export const RecommendedAnswer: FC<Props> = ({
  voiceMailData,
  customer,
  containerRef,
}) => {
  const recommended = voiceMailData.recommendedAnswer
  if (!recommended) return null

  if (recommended.errorCode) {
    const friendly = getFriendlyError(
      recommended.errorCode,
      recommended.errorMessage,
    )

    const error = new RequestError(friendly.message, {
      errorCode: recommended.errorCode,
      serverMessage: recommended.errorMessage,
      chatUid: voiceMailData.uid,
      source: 'recommended-answer',
    })
    error.name = friendly.title

    return <AskCopilotError question={t`Recommended answer`} error={error} />
  }

  return (
    <AskCopilotChat
      chat={{
        uid: voiceMailData.uid,
        organizationUid: voiceMailData.organizationUid,
        created: voiceMailData.created,
        answers: [
          {
            ...recommended,
            question: t`Recommended answer`,
            reply: buildRecommendedReply(
              voiceMailData.question,
              recommended.reply,
            ),
          },
        ],
      }}
      customer={customer}
      showThumbs
      containerRef={containerRef}
    />
  )
}
